import SlideFrame from './SlideFrame';
import type { SwimlaneCellType } from '../../i18n/types';

interface SwimlaneStep { row: number; lane: number; text: string; type: SwimlaneCellType }

interface Props {
  title: string; sub: string;
  lanes: string[];
  steps: SwimlaneStep[];
  tips?: string[];
}

const CELL_CLASS: Record<SwimlaneCellType, string> = {
  io:    'swimlane-cell--io',
  step:  'swimlane-cell--step',
  gate:  'swimlane-cell--gate',
  note:  'swimlane-cell--note',
  hooks: 'swimlane-cell--hooks',
  empty: 'swimlane-cell--empty',
};

const DownArrow = () => (
  <svg width="10" height="16" viewBox="0 0 10 16" fill="none" style={{ flexShrink: 0 }}>
    <line x1="5" y1="0" x2="5" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
    <path d="M1 9l4 6 4-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
  </svg>
);

const RightArrow = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ display: 'block' }}>
    <path d="M2 7h9M7.5 3.5L11 7l-3.5 3.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export default function Swimlane({ title, sub, lanes, steps, tips }: Props) {
  const maxRow = steps.reduce((m, s) => Math.max(m, s.row), 0);
  const grid: (SwimlaneStep | null)[][] = Array.from({ length: maxRow + 1 }, () =>
    Array(lanes.length).fill(null),
  );
  for (const step of steps) grid[step.row][step.lane] = step;

  const cols = `repeat(${lanes.length}, 1fr)`;

  // Show arrow in a lane if the current row has content in that lane,
  // OR the next row has content — indicating vertical flow
  function shouldShowArrow(ri: number, ci: number): boolean {
    const curHas = grid[ri][ci] !== null;
    const nextHas = grid[ri + 1]?.[ci] !== null;
    return curHas && nextHas;
  }

  // Any lane across current + next rows has content = show the connector row
  function rowHasAnyFlow(ri: number): boolean {
    for (let ci = 0; ci < lanes.length; ci++) {
      if (shouldShowArrow(ri, ci)) return true;
    }
    return false;
  }

  return (
    <SlideFrame title={title} sub={sub}>
      <div className="swimlane">
        {/* Lane headers */}
        <div className="swimlane-head" style={{ gridTemplateColumns: cols }}>
          {lanes.map((lane, i) => <div key={i}>{lane}</div>)}
        </div>

        {grid.map((row, ri) => (
          <div key={ri}>
            {/* Data row */}
            <div className="swimlane-row" style={{ gridTemplateColumns: cols }}>
              {row.map((cell, ci) => {
                const nextNonEmpty = row[ci + 1] !== null;
                return (
                  <div key={ci} className={`swimlane-cell ${cell ? CELL_CLASS[cell.type] : 'swimlane-cell--empty'}`}>
                    {cell && <span style={{ whiteSpace: 'pre-line' }}>{cell.text}</span>}
                    {cell && nextNonEmpty && <span className="sw-arr"><RightArrow /></span>}
                  </div>
                );
              })}
            </div>

            {/* Connector arrows between rows */}
            {ri < maxRow && rowHasAnyFlow(ri) && (
              <div className="swimlane-connector" style={{ gridTemplateColumns: cols }}>
                {Array.from({ length: lanes.length }, (_, ci) => (
                  <div
                    key={ci}
                    className={`swimlane-conn-cell${shouldShowArrow(ri, ci) ? ' swimlane-conn-cell--has-flow' : ''}`}
                  >
                    {shouldShowArrow(ri, ci) && <DownArrow />}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {tips && tips.length > 0 && (
        <div className="swimlane-tips">
          {tips.map((tip, i) => (
            <div key={i} className="swimlane-tip">💡 {tip}</div>
          ))}
        </div>
      )}
    </SlideFrame>
  );
}
